import { ChangeDetectorRef, Component, Injector, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as R from 'ramda';

import { valueAccessorFactory } from '../../../helpers';
import { KeyDownListenerService, OutsideClickListenerService } from '../../../providers';
import { DomHandler } from '../../../services';
import { hasValue } from '../../../util';
import { formControlFactory } from '../../form-control.provider';
import { BaseSelectDirective } from '../base-select.directive';
import { selectProviderFactory } from '../with-select-provider';

@Component({
  selector: 'pcs-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [
    valueAccessorFactory(MultiSelectComponent),
    KeyDownListenerService,
    OutsideClickListenerService,
    selectProviderFactory(MultiSelectComponent),
    formControlFactory(MultiSelectComponent)
  ]
})
export class MultiSelectComponent extends BaseSelectDirective {

  @Input() selected: any = [];
  @Input() showFirstOption = true;
  @Input() selectionLimit: number;
  @Input() container: 'body' | null = null;

  public showSelected = false;

  constructor(
    public translateService: TranslateService,
    public keyDownListener: KeyDownListenerService,
    public outsideClickListener: OutsideClickListenerService,
    public domHandler: DomHandler,
    public cd: ChangeDetectorRef,
    public injector: Injector
  ) {
    super(translateService, keyDownListener, outsideClickListener, domHandler, cd);
  }

  writeValue(values: any[]) {
    this.selected = [];
    if (values) {
      values.forEach(value =>
        this.updateSelectedValue(value)
      );
    }
  }

  select(option: any) {
    if (!this.disabledForSelection(option)) {
      this.updateSelectedValue(option);
    }
  }

  updateSelectedValue(option: any) {
    const isNotSelected = R.complement(R.equals(option));
    const deselectOption = R.filter(isNotSelected);
    const addToSelected = R.append(option);
    const updateSelectedOptions = this.isSelected(option) ? deselectOption : addToSelected;

    this.selected = updateSelectedOptions(this.selected);
  }

  onFocus() {
    this.show();
    this.showSelected = false;
    this.focused = true;
  }

  onBlur(event) {
    this.focused = false;
  }

  isSelected(option): boolean {
    return this.hasSelectedValues() && R.any(R.equals(option), this.selected);
  }

  selectFirstOption(): void {
    this.selectAll();
  }

  isFirstSelected(): boolean {
    return this.hasSelectedValues() && this.selected.length === this.getOptions().length;
  }

  showSelectedOptions() {
    if (this.showSelectionMenu && this.showSelected) {
      this.showSelected = false;
      this.hide();
    } else if (this.showSelectionMenu) {
      this.showSelected = true;
    } else if (!this.showSelectionMenu) {
      this.showSelected = true;
      this.show();
    }
  }

  getVisibleOptions(): any[] {
    return this.shouldShowSelected() ? this.selected : this.filteredOptions;
  }

  hasSelectedValues(): boolean {
    return hasValue(this.selected);
  }

  numberOfSelectedValues(): number {
    return R.length(this.selected);
  }

  showSelectAll(): boolean {
    return !this.remoteSearch() && this.hasAdditionalOption() && !this.selectionLimitDefined();
  }

  shouldShowSelected(): boolean {
    return this.showSelected && this.hasSelectedValues();
  }

  getLabelDetails() {
    return this.labelDetails ? this.labelDetails : this.defaultLabelDetails();
  }

  getPlaceholder() {
    const placeholder: any = R.compose(
      R.join(', '),
      R.map(selected => this.translateService.instant(this.getOptionValue(selected)))
    );
    return this.hasSelectedValues() ? placeholder(this.selected) : this.defaultPlaceholder();
  }

  disabledForSelection(option) {
    return this.selectionLimitReached() && !this.isSelected(option);
  }

  private selectionLimitDefined() {
    return hasValue(this.selectionLimit);
  }

  private selectionLimitReached() {
    return this.selectionLimitDefined() && R.equals(Number(this.selectionLimit), this.numberOfSelectedValues());
  }

  private defaultLabelDetails() {
    return this.selectionLimitDefined() && !this.readonly
      ? this.translateService.instant('label.multiselect.selectionLimit', [this.selectionLimit])
      : '';
  }

  private selectAll() {
    if (this.isFirstSelected()) {
      this.selected = [];
    } else {
      this.selected = [...this.getOptions()];
    }

    this.propagateChange(this.selected);
    this.selectionChanged.emit(this.selected);
  }

  private getOptions() {
    return this.isRemoteSearch() ? this.filteredOptions : this.options;
  }
}
